import useFetchEntity from 'hooks/useFetchEntity';
import { useEffect, useMemo, useRef } from 'react';
import IG2DayRecapCore from './Cores/IG2_DayRecap_Core';
import PhaseSelector from './Filters/PhaseSelector';

import "@fontsource/audiowide";
import "@fontsource/bebas-neue";
import "@fontsource/covered-by-your-grace";
import "@fontsource/economica";
import "@fontsource/righteous";
import "@fontsource/russo-one";

import DebugPrinter from 'components/Debug/DebugPrinter';
import DateSelector from 'components/Infographics/Filters/DateSelector';
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import usePhaseIdTranslator from 'hooks/usePhaseIdTranslator';
import usePersistentStore from 'store/persistentStore';
import BackgroundSelector from './Filters/BackgroundSelector';
import IgImgPlacement from './Filters/IgImgPlacement';
import IgImgSelector from './Filters/IgImgSelector';
import MarginEditor from './Filters/MarginEditor';
import VenueSelector from './Filters/VenueSelector';
import useZoomByMouseWheel from './Hooks/useZoomByMouseWheel';

dayjs.extend(customParseFormat);

const IG2_DayRecap = ({ showData, exportRef, onMetadataChange }) => {
  const membership = usePersistentStore((state) => state.get('ig-membership'));
  const setMembership = usePersistentStore((state) => state.set('ig-membership'));
  const date = usePersistentStore((state) => state.get('ig-date'));
  const setDate = usePersistentStore((state) => state.set('ig-date'));
  const venue = usePersistentStore((state) => state.get('ig-venue'));
  const setVenue = usePersistentStore((state) => state.set('ig-venue'));
  const fgPictureUrl = usePersistentStore((state) => state.get('ig-dayrecap-fg-url'));
  const setFgPictureUrl = usePersistentStore((state) => state.set('ig-dayrecap-fg-url'));
  const size = usePersistentStore((state) => state.get('ig-dayrecap-size'));
  const setSize = usePersistentStore((state) => state.set('ig-dayrecap-size'));
  const SIZE_PRESETS = ['PORTRAIT', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 1100, height: 1955, preset: 'PORTRAIT' };

  const fgImgPositionX = usePersistentStore((state) => state.get('ig-dayrecap-fgimgposition-x')) ?? 0;
  const setFgImgPositionX = usePersistentStore((state) => state.set('ig-dayrecap-fgimgposition-x'));
  const fgImgPositionY = usePersistentStore((state) => state.get('ig-dayrecap-fgimgposition-y')) ?? 0;
  const setFgImgPositionY = usePersistentStore((state) => state.set('ig-dayrecap-fgimgposition-y'));
  const fgImgZoom = usePersistentStore((state) => state.get('ig-dayrecap-fgzoom')) ?? 450;
  const setFgImgZoom = usePersistentStore((state) => state.set('ig-dayrecap-fgzoom'));
  const fgImgRef = useRef(null);

  const igname = 'dayrecap';
  const marginDefaults = { top: 0, left: 0, bottom: 0, right: 0 };
  const fgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setFgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));
  useZoomByMouseWheel(fgImgRef, fgImgZoom, setFgImgZoom);

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-dayrecap-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-dayrecap-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-dayrecap-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-dayrecap-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-dayrecap-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-dayrecap-background-mode'));

  const { phaseIdsAll } = usePhaseIdTranslator(membership);
  const seasonId = membership?.seasonId;

  const { data: seasonDetail } = useFetchEntity('Season', seasonId, 'Competition');

  const venueIdsAll = venue?.venueIds == null || venue?.venueIds.length === 0 ? undefined : venue?.venueIds;

  const metadata = useMemo(() => ({
    SeasonName: seasonDetail?.name,
    CompetitionName: seasonDetail?.Competition?.name,
    CompetitionLogo:
      seasonDetail?.Competition?.logoUrl?.replace(
        "[size]",
        "cropped_md",
      ),
    Date: date,
  }), [seasonDetail?.name, seasonDetail?.Competition?.name, seasonDetail?.Competition?.logoUrl, date]);

  // Notify parent component when metadata changes
  useEffect(() => {
    if (onMetadataChange && metadata.SeasonName) {
      onMetadataChange(metadata);
    }
  }, [metadata, onMetadataChange]);



  return (
    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Membership:</span>
        <PhaseSelector
          value={membership}
          onChange={setMembership}
          mandatory={['competitionId', 'seasonId']}
        />

        <span>Choose date:</span>
        <div>
          <DateSelector
            value={date}
            onChange={(dateString) => setDate(dateString ?? null)}
          />
        </div>

        <span>Foreground image:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrl} setFgPictureUrl={setFgPictureUrl} igId={2} />
        </div>

        <span className="relative top-[-14px]">Foreground placement:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionX}
            fgImgPositionY={fgImgPositionY}
            setFgImgPositionX={setFgImgPositionX}
            setFgImgPositionY={setFgImgPositionY}
            fgImgZoom={fgImgZoom}
            setFgImgZoom={setFgImgZoom}
          />
        </div>

        <span>Choose venue:</span>
        <div>
          <VenueSelector
            value={venue}
            onChange={(venue) => { setVenue(venue) }}
          />
        </div>

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={2}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={fgImgMargin}
            setMargin={setFgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <div />
      </div>

      {showData && (<DebugPrinter data={[metadata, date]}></DebugPrinter>)}

      <IG2DayRecapCore
        exportRef={exportRef}
        metadata={metadata}
        size={size}
        fgImgPositionX={fgImgPositionX}
        fgImgPositionY={fgImgPositionY}
        setFgImgPositionX={setFgImgPositionX}
        setFgImgPositionY={setFgImgPositionY}
        fgImgZoom={fgImgZoom}
        setFgImgZoom={setFgImgZoom}
        fgPictureUrl={fgPictureUrl}
        fgImgMargin={fgImgMargin}
        backgroundMode={backgroundMode}
        backgroundImgUrl={backgroundImgUrl}
        backgroundColor={backgroundColor}
        seasonId={seasonId}
        phaseIdsAll={phaseIdsAll}
        venueIdsAll={venueIdsAll}
        enablePositioning={true}
        useImageProxy={true}
      />
    </>
  )
}

export default IG2_DayRecap